import React from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  Divider,
  Paper,
} from "@mui/material";

import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";

import { styled } from "@mui/system";
import { FAQAccordion, faqs } from "./pages/Faqs";
import { useNavigate } from "react-router-dom";
import {
  Edit as CustomFormIcon,
  QrCode as QrCodeIcon,
  Analytics as AnalyticsIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";

// Add these animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};
const staggerContainer = {
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
};
const scaleUp = {
  hidden: { scale: 0.95, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { type: "spring", stiffness: 300 },
  },
};
const features = [
  {
    title: "Custom Registration Forms",
    description:
      "Decide exactly what information you need from your visitors. Tailor forms to your event's needs.",
    icon: <CustomFormIcon fontSize="large" />,
  },
  {
    title: "QR Code Generation",
    description:
      "Every visitor gets a unique QR code via email or WhatsApp for seamless event entry.",
    icon: <QrCodeIcon fontSize="large" />,
  },
  {
    title: "Footfall & Analytics",
    description:
      "Monitor visitor data in real time with insightful reports for better decision-making.",
    icon: <AnalyticsIcon fontSize="large" />,
  },
  {
    title: "Premium Entry & Payments",
    description:
      "Enable paid registrations and validate tickets automatically for a hassle-free event.",
    icon: <PaymentIcon fontSize="large" />,
  },
];
const buttonVariants = {
  hover: {
    scale: 1.05,

    transition: { type: "spring", stiffness: 300 },
  },
  tap: { scale: 0.95 },
};

const AnimatedGridItem = ({ children, index }) => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={fadeInUp}
      transition={{ delay: index * 0.1 }}
    >
      {children}
    </motion.div>
  );
};
const FeatureCard = ({ title, description, icon }) => (
  <Paper
    elevation={0}
    sx={{
      p: 4,
      height: 320, // Fixed height
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start", // Left alignment
      textAlign: "left",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 4,
      position: "relative",
      overflow: "hidden",
      "&:hover": {
        transform: "translateY(-8px)",
        boxShadow: 6,
        borderColor: "primary.main",
        "&:before": {
          opacity: 1,
        },
      },
      "&:before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
          "linear-gradient(45deg, primary.main 0%, primary.light 100%)",
        opacity: 0,
        transition: "opacity 0.3s ease",
      },
    }}
  >
    <Box
      sx={{
        width: 56,
        height: 56,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 2,
        color: "primary.main",
        mb: 3,
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 48 } })}
    </Box>
    <Typography
      variant="h6"
      fontWeight={700}
      gutterBottom
      sx={{ lineHeight: 1.3 }}
    >
      {title}
    </Typography>
    <Typography
      variant="body1"
      color="text.secondary"
      sx={{
        lineHeight: 1.6,
      }}
    >
      {description}
    </Typography>
    <Box sx={{ flexGrow: 1 }} /> {/* Spacer for equal height */}
  </Paper>
);

const HeroSection = styled(motion.div)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  padding: "80px 20px",
  background: "linear-gradient(135deg, #2A2A72, #009FFD)",
  color: "#fff",
});

const HomeContent = () => {
  const navigate = useNavigate();
  return (
    <>
      {/* Hero Section */}
      <HeroSection
        initial="hidden"
        animate="visible"
        variants={{
          visible: { transition: { staggerChildren: 0.2 } },
        }}
      >
        <motion.div variants={fadeInUp}>
          <Typography variant="h3" fontWeight="bold">
            Streamlined Visitor Management & Analytics
          </Typography>
        </motion.div>
        <motion.div variants={fadeInUp}>
          <Typography variant="h6" mt={2}>
            Simplify visitor registration and event footfall analysis with our
            all-in-one solution.
          </Typography>
        </motion.div>
        <motion.div whileHover="hover" whileTap="tap" variants={buttonVariants}>
          <Button
            variant="contained"
            sx={{
              mt: 3,
              background: "linear-gradient(45deg, #fff 30%, #f5f5f5 90%)",
              color: "#2A2A72",
              fontWeight: "bold",
              padding: "12px 32px",
              fontSize: "1.1rem",
              borderRadius: "50px",
              textTransform: "none",
              "&:hover": {
                background: "linear-gradient(45deg, #f5f5f5 30%, #fff 90%)",
              },
            }}
            onClick={() => {
              navigate("/book-trial");
            }}
          >
            Book Your Free Trial
          </Button>
        </motion.div>
      </HeroSection>

      <Container sx={{ py: 8 }}>
        <Typography variant="h4" align="center" fontWeight="bold" gutterBottom>
          Key Features
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          {features.map((feature, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={index}
              sx={{ display: "flex" }}
            >
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "0px 0px -100px 0px" }}
                transition={{ delay: index * 0.15, duration: 0.6 }}
                style={{ width: "100%" }}
              >
                <FeatureCard {...feature} />
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
      <Container>
        <Divider sx={{ my: 6 }} component={motion.hr} variants={fadeInUp} />
      </Container>

      {/* FAQ Section */}
      <Container sx={{ py: 6 }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <Typography
            variant="h4"
            textAlign="center"
            fontWeight="bold"
            gutterBottom
            component={motion.div}
            variants={fadeInUp}
          >
            Frequently Asked Questions
          </Typography>

          <Grid
            container
            spacing={2}
            mt={3}
            component={motion.div}
            variants={staggerContainer}
          >
            {faqs.map((faq, index) => (
              <Grid item xs={12} key={index}>
                <AnimatedGridItem index={index}>
                  <FAQAccordion
                    question={faq.question}
                    answer={faq.answer}
                    component={motion.div}
                    variants={{
                      hidden: { x: -50, opacity: 0 },
                      visible: { x: 0, opacity: 1 },
                    }}
                  />
                </AnimatedGridItem>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>

      <Container>
        <Divider sx={{ my: 6 }} component={motion.hr} variants={fadeInUp} />
      </Container>

      {/* Call to Action */}
      <Container sx={{ textAlign: "center", py: 8 }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={scaleUp}
        >
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Ready to Streamline Your Events?
          </Typography>
          <Typography variant="body1" mb={3}>
            Contact us to learn more about our plans, or try a demo today!
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              backgroundColor: "#2A2A72",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
              },
            }}
            component={motion.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/contactus")}
          >
            Get Started
          </Button>
        </motion.div>
      </Container>
    </>
  );
};

export default HomeContent;
